"use client";

import { useRef, useState } from "react";
import Image from "next/image";
import { motion, useReducedMotion, useScroll, useTransform, useWillChange } from "framer-motion";
import { PortableText } from "@portabletext/react";

import { GroqImage } from "@/helpers/Image";
import { measuredRichText } from "./richtext";
import ChevronCta from "./ChevronCta";
import ScrollingBanner from "@/components/pageComponents/global/ScrollingBanner";
import GradualBlur from "@/shadcn/GradualBlur";
import type { PagePatrickAffiliateSchema } from "@/sanity/pages/PagePatrickAffiliateTemplate";

import logo from "@/public/logo-black.svg";
import heroGrid from "@/public/patrick-affiliate/hero-grid.png";
import heroShadow from "@/public/patrick-affiliate/hero-shadow.png";
import heroCandles from "@/public/patrick-affiliate/hero-candles.svg";

/**
 * Hero — Figma 8871:2351 (desktop, a 1920x1053 frame) / 8873:235 (mobile, 430
 * wide). Sizes convert as px / 8, the design being drawn at the width where
 * 1rem === 8px.
 *
 * The frame is a stack of absolute layers over a fixed-height body, which is
 * how the design is built: grid → candlestick chart → shadow → copy → photo
 * marquee → blur strip.
 *
 * Entrance (framer-motion, ~2.4s, everything overlapping):
 *   grid fades → shadow blooms and settles → the chart draws itself left to
 *   right, lit at twice its resting tint, then recedes → chip, heading and CTA
 *   rise out of a blur → the highlight wash paints across its words and the
 *   tick marks drop in → the photo strip sweeps in from the right while each
 *   card unveils bottom-up and its photo settles from a zoom → the portrait
 *   rises out of its crop and sharpens. The blur strip stays static: an
 *   opacity fade on its wrapper would bound GradualBlur's backdrop-filter to
 *   the strip itself, so the photos under it would only blur once it hit 1.
 * Scroll: the chart and the copy lag the page a little and the portrait grows
 * from its feet, so the layers read at three depths.
 *
 * Every motion element is a wrapper of its own: framer writes inline
 * transform/opacity, so nothing it animates may carry a Tailwind transform or
 * opacity class (see StepsSection). Resting state === the QA-ed layout.
 */

const ease = [0.25, 0.1, 0.25, 1] as const;
const easeOutExpo = [0.16, 1, 0.3, 1] as const;
const drawEase = [0.65, 0, 0.35, 1] as const;

/** Entrance start times, seconds from mount. */
const AT = {
  grid: 0,
  shadow: 0.05,
  chart: 0.2,
  chip: 0.15,
  heading: 0.3,
  cta: 0.55,
  marquee: 0.35,
  portrait: 0.6,
} as const;

/**
 * Infinite photo marquee. Two identical groups, each carrying a trailing gap so
 * one group measures exactly N * (card + gap) — that makes `translateX(-50%)`
 * land precisely on the duplicate, with no half-gap stutter at the wrap.
 *
 * Entrance: the whole strip glides in from the right on top of its constant
 * scroll, so it visibly decelerates into cruise, while each card unveils from
 * the bottom and its photo eases out of a zoom — staggered left to right.
 */
function PhotoMarquee({
  images,
  reduceMotion,
}: {
  images: PagePatrickAffiliateSchema["heroImages"];
  reduceMotion: boolean;
}) {
  if (!images?.length) return null;
  const count = images.length;

  const group = (hidden: boolean) => (
    <div
      aria-hidden={hidden || undefined}
      className="flex shrink-0 items-center gap-[1.204rem] pr-[1.204rem] lg:gap-[1.75rem] lg:pr-[1.75rem]">
      {images.map((image, i) => {
        // The stagger continues into the duplicate group: with a short set its
        // leading cards are on screen at load and must join the same wave.
        const delay = AT.marquee + 0.1 + ((hidden ? count : 0) + i) * 0.07;
        return (
          <motion.div
            key={`${hidden ? "b" : "a"}-${i}`}
            initial={reduceMotion ? false : { clipPath: "inset(100% 0% 0% 0%)" }}
            animate={{ clipPath: "inset(0% 0% 0% 0%)" }}
            transition={{ duration: 0.9, ease: easeOutExpo, delay }}
            className="relative h-[32.339rem] w-[26.92rem] shrink-0 overflow-hidden rounded-[0.516rem] lg:h-[47rem] lg:w-[39.125rem] lg:rounded-[0.75rem]">
            <motion.div
              initial={reduceMotion ? false : { scale: 1.22 }}
              animate={{ scale: 1 }}
              transition={{ duration: 1.5, ease: easeOutExpo, delay }}
              className="h-full w-full">
              <GroqImage
                src={image}
                alt=""
                quality={100}
                // Opts out of the helper deriving style.width from the asset's pixels.
                style={{ width: "100%" }}
                className="!h-full !w-full object-cover"
              />
            </motion.div>
          </motion.div>
        );
      })}
    </div>
  );

  return (
    <div className="absolute inset-x-0 top-[49.625rem] overflow-hidden lg:top-[67rem]">
      <motion.div
        initial={reduceMotion ? false : { x: "14rem" }}
        animate={{ x: "0rem" }}
        transition={{ duration: 1.8, ease: easeOutExpo, delay: AT.marquee }}>
        <div className="patrick-hero-marquee flex w-max">
          {group(false)}
          {group(true)}
        </div>
      </motion.div>

      <style jsx>{`
        .patrick-hero-marquee {
          animation: patrick-hero-marquee-scroll 60s linear infinite;
          will-change: transform;
        }
        @keyframes patrick-hero-marquee-scroll {
          from {
            transform: translateX(0);
          }
          to {
            transform: translateX(-50%);
          }
        }
        @media (prefers-reduced-motion: reduce) {
          .patrick-hero-marquee {
            animation: none;
          }
        }
      `}</style>
    </div>
  );
}

export default function HeroSection({ data }: { data: PagePatrickAffiliateSchema }) {
  const reduceMotion = useReducedMotion() ?? false;

  // The highlight wash/ticks are CSS keyframes inside PortableText, and the
  // <style jsx> is server-inserted, so their clock would start at HTML parse —
  // ahead of framer's, which starts at hydration. Flipping this on when the
  // heading's rise starts puts both on the same clock; with reduced motion the
  // rise never starts, so the highlight simply stays in place.
  const [headingLive, setHeadingLive] = useState(false);

  // Adds will-change only while the portrait's entrance actually runs, instead
  // of holding a ~10 MB compositor layer for the life of the page.
  const portraitWillChange = useWillChange();

  const sectionRef = useRef<HTMLElement>(null);
  const { scrollYProgress } = useScroll({
    target: sectionRef,
    offset: ["start start", "end start"],
  });
  // Background lags the page the most, the copy a little, and the portrait —
  // the nearest layer — grows from its feet instead of moving: its crop ends
  // exactly where the section is clipped, so a translate would expose the cut.
  const chartY = useTransform(scrollYProgress, [0, 1], ["0rem", "12rem"]);
  const copyY = useTransform(scrollYProgress, [0, 1], ["0rem", "7rem"]);
  const portraitScale = useTransform(scrollYProgress, [0, 1], [1, 1.07]);

  if (!data?.heroChipText?.length && !data?.heroHeading?.length && !data?.heroCTA && !data?.heroImages?.length)
    return null;

  /** Rise out of a blur — the move every piece of copy makes. */
  const rise = (y: string, blur: number, delay: number, duration: number) => ({
    initial: reduceMotion ? false : { opacity: 0, y, filter: `blur(${blur}px)` },
    animate: { opacity: 1, y: "0rem", filter: "blur(0px)" },
    transition: { duration, ease, delay },
  });

  return (
    <section
      ref={sectionRef}
      className="bg-white">
      {/* Top bar — the page has no global header, and the design draws one. */}
      <div className="flex w-full items-center justify-center border-b border-black/[11%] py-[2.5rem]">
        <motion.div
          initial={reduceMotion ? false : { opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.9, ease, delay: AT.grid }}>
          <Image
            src={logo}
            alt="IQ Capital"
            className="h-[2.915rem] w-[15.738rem]"
            style={{ width: "15.738rem" }}
          />
        </motion.div>
      </div>

      <div className="relative overflow-clip">
        <div className="relative h-[86.375rem] w-full overflow-clip bg-white lg:h-[115rem]">
          <motion.div
            aria-hidden
            initial={reduceMotion ? false : { opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 1.4, ease, delay: AT.grid }}
            className="pointer-events-none absolute inset-0">
            <div
              className="absolute inset-0 opacity-20 lg:opacity-40"
              style={{
                backgroundImage: `url(${heroGrid.src})`,
                backgroundSize: "3.5rem 3.5rem",
                backgroundPosition: "top left",
              }}
            />
          </motion.div>

          {/* Candlestick chart. Figma draws it in #F5F5F5 with an overlay blend, which
            only reads against its grey shadow gradient; a flat black tint at 6% gives
            the same faint grey chart on any background.
            Entrance: it draws itself left to right at 12% (the wrapper at 1) and
            then recedes to the design's 6% (the wrapper settling at 0.5). */}
          <div
            aria-hidden
            className="pointer-events-none absolute top-0 right-0 hidden h-[97.375rem] w-[94.25rem] rotate-[-0.21deg] lg:block">
            <motion.div
              className="h-full w-full"
              style={reduceMotion ? undefined : { y: chartY }}>
              <motion.div
                initial={reduceMotion ? false : { clipPath: "inset(0% 100% 0% 0%)", opacity: 0 }}
                animate={{
                  clipPath: "inset(0% 0% 0% 0%)",
                  opacity: [0, 1, 1, 0.5],
                }}
                transition={{
                  clipPath: { duration: 2.2, ease: drawEase, delay: AT.chart },
                  opacity: {
                    duration: 2.8,
                    times: [0, 0.15, 0.7, 1],
                    ease: "easeInOut",
                    delay: AT.chart,
                  },
                }}
                className="h-full w-full">
                <div className="h-full w-full opacity-[0.12]">
                  <Image
                    src={heroCandles}
                    alt=""
                    className="h-full w-full"
                    style={{ width: "100%" }}
                  />
                </div>
              </motion.div>
            </motion.div>
          </div>

          {/* The blend lives on the wrapper so it keeps blending with the grid
            while the wrapper animates; the image's 40% × the wrapper's resting
            0.5 is the design's 20%. It blooms to 30% on the way in. */}
          <motion.div
            aria-hidden
            initial={reduceMotion ? false : { opacity: 0, scale: 1.08 }}
            animate={{ opacity: [0, 0.75, 0.5], scale: 1 }}
            transition={{
              opacity: {
                duration: 2.4,
                times: [0, 0.45, 1],
                ease: "easeInOut",
                delay: AT.shadow,
              },
              scale: { duration: 2.4, ease: easeOutExpo, delay: AT.shadow },
            }}
            className="pointer-events-none absolute inset-0 mix-blend-hard-light">
            <Image
              src={heroShadow}
              alt=""
              className="!h-full !w-full object-cover opacity-40"
              style={{ width: "100%" }}
            />
          </motion.div>

          {/* 40px under the top bar, as the mobile frame draws it. */}
          <div className="absolute inset-x-0 top-[5rem] flex flex-col items-center px-[1.25rem] lg:top-[7.125rem] lg:px-0">
            <motion.div
              className="flex w-full flex-col items-center gap-[4rem] lg:w-[144.625rem] lg:gap-[4.25rem]"
              style={reduceMotion ? undefined : { y: copyY }}>
              <div className="flex w-full flex-col items-center gap-[1.5rem]">
                {data?.heroChipText?.length > 0 && (
                  <motion.div
                    {...rise("1.5rem", 6, AT.chip, 0.8)}
                    className="w-full">
                    <div className="richtext richtext-center prose-p:font-blacker prose-p:text-[1.875rem] prose-p:leading-[1.5] prose-p:text-black prose-p:italic lg:prose-p:text-[2rem]">
                      <PortableText
                        value={data.heroChipText}
                        components={measuredRichText}
                      />
                    </div>
                  </motion.div>
                )}
                {/* Highlight: Figma draws this one with a 79px wash and an 85px left
                  rule on desktop (74 / 74 elsewhere), and types two spaces next to
                  the ticks — 1 before / 2 after at desktop, 2 / 2 on mobile.
                  --hl-mr also has to clear the right tick, which reserves no room
                  of its own: at 0.09em the rule cut ~9px into the "c" of
                  "competitive" (Figma leaves 1.6px). --hl-pr cannot do it — it
                  moves the wash and the tick along with it. --hl-ctr is off here
                  because the highlight sits mid-sentence, not on a line of its own. */}
                {data?.heroHeading?.length > 0 && (
                  <motion.div
                    {...rise("3rem", 12, AT.heading, 1.05)}
                    onAnimationStart={() => setHeadingLive(true)}
                    className="w-full">
                    <div className={`${headingLive ? "is-live " : ""}patrick-hero-heading richtext richtext-center [--hl-ctr:0px] [--hl-ext:0.4925rem] [--hl-ml:0.17em] [--hl-mr:0.21em] lg:[--hl-ext:0.75rem] lg:[--hl-ml:0.067em] lg:[--hl-mr:0.228em] lg:[--hl-pr:0.26em] lg:[--hl-wash:9.875rem] prose-headings:font-instrument-sans prose-headings:font-[500] prose-headings:text-[5.5rem] prose-headings:leading-[1.07] prose-headings:tracking-[-0.275rem] prose-headings:text-black lg:prose-headings:text-[10.5rem] lg:prose-headings:leading-[1.04] lg:prose-headings:tracking-[-0.525rem]`}>
                      <PortableText
                        value={data.heroHeading}
                        components={measuredRichText}
                      />
                    </div>
                  </motion.div>
                )}
              </div>

              {/* `empty:hidden`: ChevronCta renders nothing without a label, and an
                empty wrapper would still open the column gap above it. */}
              {data?.heroCTA && (
                <motion.div
                  {...rise("2rem", 10, AT.cta, 0.9)}
                  className="flex w-full flex-col items-center empty:hidden">
                  <ChevronCta
                    data={data?.heroCTA}
                    language={data?.settings?.language}
                  />
                </motion.div>
              )}
            </motion.div>
          </div>

          <PhotoMarquee
            images={data?.heroImages}
            reduceMotion={reduceMotion}
          />

          {/* Fades the marquee into the pills strip. The wrapper carries the
            design's colour wash and the responsive box; GradualBlur ramps the
            blur inside it, which a single backdrop-blur value cannot do.
            Deliberately not animated: opacity < 1 on an ancestor makes it the
            backdrop root, so the blur would stop reaching the photos below. */}
          <div className="pointer-events-none absolute inset-x-0 top-[71.415rem] h-[20.5rem] lg:top-[98.25rem] lg:h-[25.375rem]">
            <div
              aria-hidden
              className="absolute inset-0"
              style={{
                backgroundImage: "linear-gradient(180deg, rgba(255,255,255,0) 0.38%, rgba(9,24,20,0.3) 99.6%)",
              }}
            />
            <GradualBlur
              target="parent"
              position="bottom"
              height="100%"
              strength={2}
              divCount={6}
              curve="bezier"
              exponential
              opacity={1}
              zIndex={2}
            />
          </div>
        </div>

        <ScrollingBanner
          className="h-[8.625rem] border-y-0 border-t border-black/10"
          gapRem={6}
          separatorClassName="h-[0.5rem] w-[0.5rem] rounded-none"
          words={data?.scrollingWords?.words}
          wordsI18n={data?.scrollingWords?.wordsI18n}
        />

        {/* Sits in front of the marquee and, on desktop, over the pills strip —
            hence living outside the body, above ScrollingBanner's own z-10.
            Mobile ends flush with the body, so nothing overlaps there.
            The inner crop is the design's, and deliberately off-centre:
            `object-cover` would centre it and shift the figure ~49px left.
            The crop box scales on scroll and the figure rises inside it on
            entry; the centring translate stays on the outer, static box. */}
        {data?.heroPortrait && (
          <div className="pointer-events-none absolute top-[38.835rem] left-1/2 z-20 h-[47.459rem] w-[55.537rem] -translate-x-1/2 lg:top-[47rem] lg:h-[82.25rem] lg:w-[96.25rem]">
            <motion.div
              className="absolute inset-0 overflow-hidden"
              style={reduceMotion ? undefined : { scale: portraitScale, transformOrigin: "50% 100%" }}>
              <motion.div
                initial={reduceMotion ? false : { opacity: 0, y: "16%", scale: 1.05, filter: "blur(28px)" }}
                animate={{ opacity: 1, y: "0%", scale: 1, filter: "blur(0px)" }}
                transition={{
                  duration: 1.5,
                  ease: easeOutExpo,
                  delay: AT.portrait,
                }}
                className="absolute inset-0"
                style={{
                  transformOrigin: "50% 100%",
                  willChange: portraitWillChange,
                }}>
                <GroqImage
                  src={data.heroPortrait}
                  alt=""
                  quality={100}
                  // Defining style.width opts out of the helper's px/8 sizing.
                  style={{ width: "123.39%" }}
                  className="absolute top-[-3.65%] left-[-5.32%] !h-[103.65%] !w-[123.39%] max-w-none"
                />
              </motion.div>
            </motion.div>
          </div>
        )}
      </div>

      {/* The measured highlight joins the choreography: once the heading has
          landed the wash paints left to right, the left tick drops in ahead of
          it and the right tick lands as the paint arrives. Hooks are the
          additive `hl-*` classes in richtext.tsx; `.is-live` lands when the
          heading's rise starts, so the delays below share framer's clock. */}
      <style jsx>{`
        .patrick-hero-heading.is-live :global(.hl-wash),
        .patrick-hero-heading.is-live :global(.hl-texture) {
          transform-origin: 0 50%;
          animation: patrick-hl-paint 0.75s cubic-bezier(0.65, 0, 0.35, 1) 1.15s both;
        }
        .patrick-hero-heading.is-live :global(.hl-tick-l) {
          animation: patrick-hl-tick 0.5s cubic-bezier(0.16, 1, 0.3, 1) 1.05s both;
        }
        .patrick-hero-heading.is-live :global(.hl-tick-r) {
          animation: patrick-hl-tick 0.5s cubic-bezier(0.16, 1, 0.3, 1) 1.8s both;
        }
        @keyframes patrick-hl-paint {
          from {
            transform: scaleX(0);
          }
          to {
            transform: scaleX(1);
          }
        }
        @keyframes patrick-hl-tick {
          from {
            opacity: 0;
            transform: translateY(0.75rem);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        @media (prefers-reduced-motion: reduce) {
          .patrick-hero-heading :global(.hl-wash),
          .patrick-hero-heading :global(.hl-texture),
          .patrick-hero-heading :global(.hl-tick-l),
          .patrick-hero-heading :global(.hl-tick-r) {
            animation: none;
          }
        }
      `}</style>
    </section>
  );
}
